/**
 * Conditions Section Component (Internal Portal)
 *
 * Displays a group of conditions (PTD, PTF, or AC) with their status,
 * download buttons (where templates exist), and upload buttons.
 * Conditions are grouped by owner role (Processor → Loan Officer → Underwriter → Other).
 *
 * Used by InternalConditionsPortal only. External portals use
 * TitleEscrowConditionsSection or BorrowerConditionsSection.
 */

import { CheckCircle2, Circle, User, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { EncompassCondition } from '@/types/approval-letter';
import { ConditionCard } from './ConditionCardShared';
import type { ConditionsSectionProps } from './ConditionCardShared';

// Fixed order for owner role groups
const ROLE_ORDER = ['Processor', 'Loan Officer', 'Underwriter'];

// Role-specific color mapping for visual distinction
const ROLE_COLORS: Record<string, { border: string; bg: string; text: string; icon: string }> = {
  Processor: {
    border: 'border-l-blue-500',
    bg: 'bg-blue-50/50',
    text: 'text-blue-700',
    icon: 'text-blue-500'
  },
  'Loan Officer': {
    border: 'border-l-purple-500',
    bg: 'bg-purple-50/50',
    text: 'text-purple-700',
    icon: 'text-purple-500'
  },
  Underwriter: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-50/50',
    text: 'text-amber-700',
    icon: 'text-amber-500'
  },
  default: {
    border: 'border-l-gray-400',
    bg: 'bg-gray-50/50',
    text: 'text-gray-600',
    icon: 'text-gray-400'
  }
};

/**
 * Get role colors based on owner name
 */
function getRoleColors(ownerName: string) {
  if (ownerName === 'Unassigned') {
return ROLE_COLORS['default'];
}
  if (ownerName.toLowerCase().includes('processor')) {
return ROLE_COLORS['Processor'];
}
  if (ownerName.toLowerCase().includes('loan officer')) {
return ROLE_COLORS['Loan Officer'];
}
  if (ownerName.toLowerCase().includes('underwriter')) {
return ROLE_COLORS['Underwriter'];
}
  return ROLE_COLORS['default'];
}

/**
 * Group conditions by owner name
 */
function groupConditionsByOwner(conditions: EncompassCondition[]): Record<string, EncompassCondition[]> {
  return conditions.reduce((groups, condition) => {
    const ownerName = condition.owner?.entityName || 'Unassigned';
    if (!groups[ownerName]) {
groups[ownerName] = [];
}
    groups[ownerName].push(condition);
    return groups;
  }, {} as Record<string, EncompassCondition[]>);
}

/**
 * Sort owner names by fixed role order
 */
function sortOwnersByRole(ownerNames: string[]): string[] {
  return ownerNames.sort((a, b) => {
    const aIndex = ROLE_ORDER.findIndex(r => a.toLowerCase().includes(r.toLowerCase()));
    const bIndex = ROLE_ORDER.findIndex(r => b.toLowerCase().includes(r.toLowerCase()));
    // Unknown roles and "Unassigned" go to end
    const aOrder = aIndex === -1 ? (a === 'Unassigned' ? ROLE_ORDER.length + 1 : ROLE_ORDER.length) : aIndex;
    const bOrder = bIndex === -1 ? (b === 'Unassigned' ? ROLE_ORDER.length + 1 : ROLE_ORDER.length) : bIndex;
    return aOrder - bOrder;
  });
}

export const ConditionsSection = ({
  title,
  conditions,
  templates,
  uploads,
  notes,
  onDownloadTemplate,
  onUploadClick,
  onFillAndSign,
  borrowerPairs
}: ConditionsSectionProps) => {
  const openCount = conditions.filter(c => c.statusOpen).length;
  const allCleared = openCount === 0;

  // Group conditions by owner and sort groups by role order
  const groupedConditions = groupConditionsByOwner(conditions);
  const sortedOwnerNames = sortOwnersByRole(Object.keys(groupedConditions));

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {allCleared ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <Circle className="h-5 w-5 text-amber-500" />
          )}
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <Badge
          variant="secondary"
          className={allCleared ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
        >
          {allCleared ? 'All Cleared' : `${openCount} Open`}
        </Badge>
      </div>

      {/* Conditions grouped by owner */}
      <div className="space-y-4 pl-7">
        {sortedOwnerNames.map((ownerName) => {
          const ownerConditions = groupedConditions[ownerName];
          const ownerOpenCount = ownerConditions.filter(c => c.statusOpen).length;
          const isUnassigned = ownerName === 'Unassigned';
          const roleColors = getRoleColors(ownerName);

          return (
            <div
              key={ownerName}
              className={`
                rounded-lg border-l-4 ${roleColors.border} ${roleColors.bg}
                p-3 space-y-3
              `}
            >
              {/* Owner Group Header */}
              <div className="flex items-center gap-2">
                {isUnassigned ? (
                  <HelpCircle className={`h-4 w-4 ${roleColors.icon}`} />
                ) : (
                  <User className={`h-4 w-4 ${roleColors.icon}`} />
                )}
                <span className={`font-semibold ${roleColors.text}`}>
                  {ownerName}
                </span>
                <span className={`text-sm ${roleColors.text} opacity-70`}>
                  ({ownerOpenCount > 0 ? `${ownerOpenCount} open` : 'all cleared'})
                </span>
              </div>

              {/* Conditions in this owner group */}
              <div className="space-y-2 pl-2">
                {ownerConditions.map((condition) => {
                  const conditionKey = condition.externalId || condition.id || condition.internalId || '';
                  return (
                    <ConditionCard
                      key={condition.id}
                      condition={condition}
                      template={templates[condition.externalId]}
                      uploads={uploads.filter(u => u.conditionExternalId === condition.externalId)}
                      note={notes?.[conditionKey]}
                      onDownloadTemplate={onDownloadTemplate}
                      onUploadClick={onUploadClick}
                      onFillAndSign={onFillAndSign}
                      borrowerPairs={borrowerPairs}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConditionsSection;
